import { FunctionComponent, Fragment } from 'preact';
import { useCallback, useMemo } from 'preact/hooks';
import { formatDuration } from '@vgtv/time-helpers';
import type { Asset } from '@schibsted-svp/svp-api-types';

import { AssetThumbnail } from '../AssetThumbnail/AssetThumbnail';
import { Typography } from '../Typography/Typography';
import { getImageSrc } from '../../utils/image';
import { useTranslation } from '../../../../utils/useTranslation';
import { getSubscriptionLabel } from '../../../../utils/subscription';
import ns from '../../translations';
import cn from 'classnames';
import { isCurrentlyLive, isAssetSponsored } from '../../utils/assetHelpers';
import { AssetContext, getAssetLabel } from '../../utils/assetLabel';
import { useProvider } from '../../hooks/useProvider';
import { AdBadge } from '../AdBadge/AdBadge';
import {
  getAssetAccess,
  getAssetImage,
  isSportAccessLevel,
} from '../../../../utils/player/asset';
import { getAssetAgeLimit } from '../../utils/ageLimit';
import { AgeLimitLabel } from '../AgeLimitLabel/AgeLimitLabel';
import { useAccessDefinitions } from '../../hooks/AccessDefinitionsProvider';
import { Tag } from '../Tag/Tag';

interface AssetHorizontalProps {
  onAssetClick?: (asset: Asset, event: MouseEvent) => void;
  asset: Asset;
  width: number;
  disableDescription?: boolean;
  context?: AssetContext;
}

export const AssetHorizontal: FunctionComponent<AssetHorizontalProps> = ({
  onAssetClick,
  asset,
  width,
  disableDescription,
  context,
}) => {
  const { t } = useTranslation(ns);
  const { title, description, duration } = asset;
  const label = getAssetLabel(asset, context);
  const { assetProvider, configProvider } = useProvider();
  const { accessDefinitions } = useAccessDefinitions();
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (onAssetClick) {
        onAssetClick(asset, e);
      }
    },
    [onAssetClick, asset]
  );

  const showDescription = description && !disableDescription;

  const poster = getAssetImage(asset, 'main');

  const imageSrc = useMemo(() => {
    return getImageSrc(poster, width);
  }, [poster, width]);

  const accessLevel = getAssetAccess(asset);

  const sponsoredBadge = useMemo(
    () =>
      isAssetSponsored(assetProvider, asset) ? (
        <span className="vgp-asset-sponsored">
          <AdBadge />
        </span>
      ) : null,
    [asset, assetProvider]
  );

  const primaryBadge = duration ? (
    <Typography variant="label" size="medium" color="subtitle-text">
      <Tag>{formatDuration(duration)}</Tag>
    </Typography>
  ) : null;

  let secondaryBadge = isSportAccessLevel(configProvider, accessLevel) ? (
    <Typography variant="label" size="medium" color="subtitle-text">
      <Tag>{getSubscriptionLabel(accessDefinitions, accessLevel)}</Tag>
    </Typography>
  ) : null;

  const ageLimit = getAssetAgeLimit(asset);
  if (ageLimit !== null) {
    const labelTag = <AgeLimitLabel ageLimit={ageLimit} />;
    secondaryBadge = secondaryBadge ? (
      <Fragment>
        {labelTag}&nbsp;·&nbsp;{secondaryBadge}
      </Fragment>
    ) : (
      labelTag
    );
  }

  const tertiaryBadge = isCurrentlyLive(asset) ? (
    <span className="vgp-asset-live-badge">
      <i className="vgp-asset-live-icon" />
      <Typography variant="label" size="medium" bold data-skin="breaking">
        {t('LIVE')}
      </Typography>
    </span>
  ) : (
    sponsoredBadge
  );

  return (
    <div className="vgp-asset" onClick={handleClick}>
      <AssetThumbnail
        imageSrc={imageSrc}
        secondaryBadge={secondaryBadge}
        primaryBadge={primaryBadge}
        tertiaryBadge={tertiaryBadge}
      />
      <div
        className={cn('vgp-asset-details', {
          'vgp-asset-details--small': !showDescription,
        })}
      >
        {showDescription && (
          <Typography
            variant="title"
            size="small"
            color="primary"
            bold
            className="vgp-asset-label"
            title={label}
          >
            {label}
          </Typography>
        )}
        <Typography
          variant="title"
          size="small"
          color="primary"
          title={title}
          className={cn('vgp-asset-headline', {
            'vgp-asset-headline--small': !showDescription,
          })}
        >
          {title}
        </Typography>
        {showDescription && (
          <Typography
            variant="label"
            size="medium"
            color="primary"
            className="vgp-asset-description"
          >
            {description}
          </Typography>
        )}
      </div>
    </div>
  );
};
